import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Alert, CircularProgress, Stack, IconButton, Tooltip,
} from '@mui/material';
import { Add, Verified, Delete, Star } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';

function DnsInstructions({ dns }) {
  if (!dns) return null;
  return (
    <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: 12, mt: 1 }}>
      <Box mb={1}><strong>CNAME Record:</strong><br />
        Name: {dns.cname?.name}<br />Value: {dns.cname?.value}<br />TTL: {dns.cname?.ttl}
      </Box>
      <Box><strong>TXT Verification:</strong><br />
        Name: {dns.txt?.name}<br />Value: {dns.txt?.value}<br />TTL: {dns.txt?.ttl}
      </Box>
    </Box>
  );
}

function AddDomainDialog({ tenantId, tenantName, open, onClose, onAdded }) {
  const [domain, setDomain] = useState('');
  const [type, setType] = useState('custom');
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setDomain(''); setResult(null); setErr(''); } }, [open]);

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      const { data } = await api.post(`/superadmin/tenants/${tenantId}/domains`, { domain, type });
      setResult(data.data);
      onAdded();
    } catch(e) { setErr(e.response?.data?.message || 'Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Domain — {tenantName}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {!result ? (
          <Stack spacing={2}>
            <TextField select fullWidth size="small" label="Type" value={type} onChange={e => setType(e.target.value)}>
              <MenuItem value="custom">Custom Domain</MenuItem>
              <MenuItem value="subdomain">Subdomain</MenuItem>
            </TextField>
            <TextField fullWidth size="small" label="Domain (e.g. myshop.com)" value={domain} onChange={e => setDomain(e.target.value)} />
          </Stack>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>Domain added! Configure DNS records below to verify.</Alert>
            <Typography variant="body2" fontWeight={600} mb={1}>DNS Configuration:</Typography>
            <DnsInstructions dns={result.dns_instructions} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && <Button variant="contained" onClick={submit} disabled={saving || !domain}>{saving ? 'Adding...' : 'Add Domain'}</Button>}
      </DialogActions>
    </Dialog>
  );
}

export default function DomainsPage() {
  const [tenants, setTenants] = useState([]);
  const [domains, setDomains] = useState({});
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/superadmin/tenants', { params: { limit: 200 } });
      setTenants(data.data);
      // Load all domains
      const allDomains = {};
      await Promise.all(data.data.map(async t => {
        try {
          const r = await api.get(`/superadmin/tenants/${t.id}/domains`);
          if (r.data.data?.length > 0) allDomains[t.id] = r.data.data;
        } catch {}
      }));
      setDomains(allDomains);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const verify = async (tenantId, domainId) => {
    await api.patch(`/superadmin/tenants/${tenantId}/domains/${domainId}/verify`);
    loadTenants();
  };

  const setPrimary = async (tenantId, domainId) => {
    await api.patch(`/superadmin/tenants/${tenantId}/domains/${domainId}/primary`);
    loadTenants();
  };

  const remove = async (tenantId, domainId) => {
    if (!window.confirm('Remove this domain?')) return;
    await api.delete(`/superadmin/tenants/${tenantId}/domains/${domainId}`);
    loadTenants();
  };

  const tenantsWithDomains = tenants.filter(t => domains[t.id]?.length > 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Domain Management</Typography>
          <Typography variant="body2" color="text.secondary">Custom domains and subdomains per tenant</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField select size="small" label="Select Tenant" value={selectedTenant?.id || ''} onChange={e => setSelectedTenant(tenants.find(t => t.id === +e.target.value))} sx={{ width: 250 }}>
            {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddOpen(true)} disabled={!selectedTenant}>Add Domain</Button>
        </Box>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {tenantsWithDomains.length === 0 && !loading && (
        <Card><CardContent><Typography color="text.secondary" align="center" py={4}>No custom domains configured yet.</Typography></CardContent></Card>
      )}

      {tenantsWithDomains.map(t => (
        <Card key={t.id} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>{t.name}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Domain</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Verification</TableCell>
                  <TableCell>SSL</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {domains[t.id]?.map(d => (
                  <TableRow key={d.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={500}>{d.domain}</Typography></TableCell>
                    <TableCell><Chip label={d.type} size="small" /></TableCell>
                    <TableCell>
                      <Chip label={d.verification_status} size="small"
                        color={d.verification_status === 'verified' ? 'success' : d.verification_status === 'failed' ? 'error' : 'warning'} />
                    </TableCell>
                    <TableCell>
                      <Chip label={d.ssl_status} size="small"
                        color={d.ssl_status === 'active' ? 'success' : d.ssl_status === 'failed' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell>{d.is_primary ? <Chip label="Primary" size="small" color="primary" /> : '—'}</TableCell>
                    <TableCell><Typography variant="caption">{new Date(d.created_at).toLocaleDateString('en-GB')}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {d.verification_status !== 'verified' && (
                          <Tooltip title="Mark Verified">
                            <IconButton size="small" color="success" onClick={() => verify(t.id, d.id)}><Verified fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {!d.is_primary && (
                          <Tooltip title="Set Primary">
                            <IconButton size="small" color="primary" onClick={() => setPrimary(t.id, d.id)}><Star fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => remove(t.id, d.id)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}

      {selectedTenant && (
        <AddDomainDialog
          tenantId={selectedTenant.id}
          tenantName={selectedTenant.name}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onAdded={loadTenants}
        />
      )}
    </Box>
  );
}
